import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { websiteApi } from '../api/website.api';
import { billingApi, formatInrFromPaise } from '../api/billing.api';
import { getLiveUrl, verifiedCustomDomain } from '../utils/websiteUrls';
import Logo from '../components/Logo';
import usePageMeta from '../hooks/usePageMeta';
import useInvoiceDownload from '../hooks/useInvoiceDownload';

const PaymentSuccessPage: React.FC = () => {
  usePageMeta({ title: 'Website published — OnTheWeb', index: false });

  const { download, downloadingId } = useInvoiceDownload();
  const { websiteId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const invoiceId = searchParams.get('invoiceId') || '';
  const orderId = searchParams.get('order_id') || '';
  const [verifying, setVerifying] = useState(!!orderId && !invoiceId);
  const [resolvedInvoiceId, setResolvedInvoiceId] = useState(invoiceId);
  const [error, setError] = useState('');

  const { data: website } = useQuery({
    queryKey: ['website', websiteId],
    queryFn: () => websiteApi.getWebsiteById(websiteId),
    enabled: Boolean(websiteId),
  });

  const { data: invoice } = useQuery({
    queryKey: ['invoice', resolvedInvoiceId],
    queryFn: () => billingApi.getInvoice(resolvedInvoiceId),
    enabled: Boolean(resolvedInvoiceId),
  });

  useEffect(() => {
    if (!orderId || invoiceId || !websiteId) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await billingApi.verify({ websiteId, orderId });
        if (!cancelled) {
          setResolvedInvoiceId(result.invoiceId);
          navigate(`/website/${websiteId}/published?invoiceId=${result.invoiceId}`, { replace: true });
        }
      } catch (err: any) {
        if (!cancelled) setError(err.response?.data?.message || 'We received your payment but could not confirm it yet. Check Invoices in a moment.');
      } finally {
        if (!cancelled) setVerifying(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderId, invoiceId, websiteId, navigate]);

  const liveUrl = website?.slug ? getLiveUrl(website.slug, verifiedCustomDomain(website as any)) : '';

  return (
    <div className="nova-page min-h-screen flex items-center justify-center px-4 py-16">
      <div className="max-w-lg w-full text-center">
        <Logo />
        <div className="mt-10 nova-card p-8">
          <div className="mx-auto w-16 h-16 rounded-full bg-nova-green/20 flex items-center justify-center mb-6">
            <svg className="w-8 h-8 text-nova-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="section-tag mb-2">You&apos;re live</p>
          <h1 className="font-display text-3xl font-extrabold text-nova-text mb-3">Website published</h1>
          <p className="text-nova-text2 mb-6">
            {verifying
              ? 'Confirming your payment and publishing the site...'
              : 'Payment is complete. Your website is public and ready for visitors.'}
          </p>
          {error && <p className="text-sm text-nova-red mb-4">{error}</p>}
          {liveUrl && (
            <a href={liveUrl} target="_blank" rel="noreferrer" className="block font-mono text-sm text-nova-sky break-all mb-6">
              {liveUrl}
            </a>
          )}
          {invoice && (
            <p className="text-sm text-nova-text3 mb-6">
              Invoice {invoice.invoiceNumber} · {formatInrFromPaise(invoice.totalPaise)}
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {liveUrl && (
              <a href={liveUrl} target="_blank" rel="noreferrer" className="btn-primary justify-center">
                View live site
              </a>
            )}
            {resolvedInvoiceId && invoice && (
              <button
                type="button"
                className="btn-ghost justify-center"
                disabled={downloadingId === resolvedInvoiceId}
                onClick={() => download(resolvedInvoiceId, invoice.invoiceNumber)}
              >
                {downloadingId === resolvedInvoiceId ? 'Preparing...' : 'Download invoice (PDF)'}
              </button>
            )}
          </div>
          <div className="mt-6 flex justify-center gap-4 text-sm">
            <Link to={`/website/${websiteId}`} className="text-nova-sky">Edit website</Link>
            <Link to="/billing" className="text-nova-sky">All invoices</Link>
            <Link to="/dashboard" className="text-nova-text3">Dashboard</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
